/**
 * Translation information.
 */
export type Translation = Readonly<{
  detectedSourceLanguage: string;
  model: string;
  translatedText: string;
}>;

/**
 * Response from Google Translate V2 endpoint.
 */
export type TranslationResponse = Readonly<{
  data: {
    translations: Translation[];
  };
}>;

/**
 * Google Translate client.
 */
export class GoogleTranslate {
  private url: string =
    'https://translation.googleapis.com/language/translate/v2';

  constructor(private apiKey: string) {
    this.apiKey = apiKey;
  }

  /**
   * Call Google Translate with the given text, translating into the given
   * language. Optionally provide a source language. If no source is provided,
   * then it will be auto-detected by Google.
   */
  public async translate(text: string, into: string = 'en', source?: string) {
    const parts = [
      ['key', this.apiKey],
      ['format', 'text'],
      ['target', into],
      ['q', text],
    ];

    if (source) {
      parts.push(['source', source]);
    }

    const req = parts
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');

    const url = `${this.url}?${req}`;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'force-cache',
      redirect: 'follow',
    });

    if (result.status !== 200) {
      throw new Error(result.statusText);
    }

    return (await result.json()) as TranslationResponse;
  }
}
